import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import { lkStatusPillClass, type LkPublicationStatus } from '../../data/legalKnowledge';
import {
  fetchExtractedCaseOutcomeById,
  resolveCaseLinkageTargetIds,
  type ExtractedCaseOutcomeDetail,
} from '../../services/legalKnowledgeArchive';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { SectionCard } from '../wizard/WizardComponents';
import { CaseLinkageLink } from './CaseLinkageLink';

const GRID_STYLE = { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 } as const;

function FieldBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--t3)', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500 }}>{children}</div>
    </div>
  );
}

function dash(value: string | null | undefined): string {
  if (!value || !value.trim()) return '—';
  return value;
}

export default function ViewArchivedCaseDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ExtractedCaseOutcomeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkageTargetIds, setLinkageTargetIds] = useState<Record<string, string>>({});
  const [linkageTargetsLoading, setLinkageTargetsLoading] = useState(false);

  const lkStatusT = useCallback(
    (s: LkPublicationStatus) => t(`legalKnowledge.status.${s}`),
    [t],
  );

  useEffect(() => {
    if (!id) return;
    let active = true;
    setLoading(true);
    setError(null);
    fetchExtractedCaseOutcomeById(id)
      .then((data) => {
        if (!active) return;
        setDetail(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : t('legalKnowledge.viewArchived.loadFailed'));
        setDetail(null);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, t]);

  useEffect(() => {
    if (!detail?.case_linkages.length) {
      setLinkageTargetIds({});
      setLinkageTargetsLoading(false);
      return;
    }
    let active = true;
    setLinkageTargetsLoading(true);
    void resolveCaseLinkageTargetIds(detail.case_linkages)
      .then((targets) => {
        if (active) setLinkageTargetIds(targets);
      })
      .finally(() => {
        if (active) setLinkageTargetsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [detail?.case_linkages]);

  if (loading) {
    return (
      <>
        <div className="bc">
          <Link to="/legal-knowledge/archive">{t('legalKnowledge.archive.breadcrumb')}</Link>
          <span>/</span>
          <span>{t('legalKnowledge.viewArchived.breadcrumb')}</span>
        </div>
        <div style={{ padding: '24px 0', color: 'var(--t3)' }}>{t('common.loading')}</div>
      </>
    );
  }

  if (!detail || error) {
    return (
      <>
        <div className="bc">
          <Link to="/legal-knowledge/archive">{t('legalKnowledge.archive.breadcrumb')}</Link>
          <span>/</span>
          <span>{t('legalKnowledge.viewArchived.breadcrumb')}</span>
        </div>
        <div style={{ padding: '24px 0', fontSize: 14, color: 'var(--text)' }}>
          {error ?? t('legalKnowledge.viewOutcome.recordNotFound')}{' '}
          <Link to="/legal-knowledge/archive" style={{ color: 'var(--blue)' }}>
            {t('legalKnowledge.viewArchived.backToArchive')}
          </Link>
        </div>
      </>
    );
  }

  const status = detail.status_code.toLowerCase() as LkPublicationStatus;
  const isRejected = status === 'rejected';
  const showCourtFields = detail.file_category === 'Civil Division - Advise';
  const showLitigationFields = detail.file_category?.startsWith('Civil Division - Litigation');
  const originLabel = detail.division_name
    ? t('legalKnowledge.viewOutcome.divisionName')
    : t('legalKnowledge.viewOutcome.submittedAgency');
  const originValue = detail.division_name ?? detail.submitted_agency;

  return (
    <>
      <div className="bc">
        <Link to="/legal-knowledge/archive">{t('legalKnowledge.archive.breadcrumb')}</Link>
        <span>/</span>
        <span>{t('legalKnowledge.viewArchived.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('legalKnowledge.viewArchived.title')}</div>
        </div>
        <button type="button" className="mfb mfb-dk" onClick={() => navigate(-1)}>
          {t('legalKnowledge.viewArchived.back')}
        </button>
      </div>

      <SectionCard title={t('legalKnowledge.viewOutcome.caseOverview')}>
        <div style={GRID_STYLE}>
          <FieldBlock label={t('legalKnowledge.viewOutcome.archiveReferenceNumber')}>
            {detail.archive_reference_number}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.fileReferenceNumber')}>
            {detail.file_reference_number}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.caseCompletedAt')}>
            {detail.case_completed_at ? formatDisplayDateTime(detail.case_completed_at) : '—'}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.outcomeExtractedAt')}>
            {detail.outcome_extracted_at ? formatDisplayDateTime(detail.outcome_extracted_at) : '—'}
          </FieldBlock>

          <FieldBlock label={t('common.status')}>
            <span className={lkStatusPillClass(status)}>{lkStatusT(status)}</span>
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.fileCategory')}>{detail.file_category}</FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.subFileCategory')}>
            {dash(detail.sub_file_category)}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.applicableAreas')}>
            {dash(detail.applicable_areas)}
          </FieldBlock>

          <FieldBlock label={t('legalKnowledge.viewOutcome.requestSubmittedAt')}>
            {detail.request_submitted_at ? formatDisplayDateTime(detail.request_submitted_at) : '—'}
          </FieldBlock>
          <FieldBlock label={originLabel}>
            {dash(originValue)}
          </FieldBlock>
          <div style={{ gridColumn: 'span 2' }}>
            <FieldBlock label={t('legalKnowledge.viewOutcome.description')}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{dash(detail.description)}</span>
            </FieldBlock>
          </div>

          <FieldBlock label={t('legalKnowledge.viewOutcome.province')}>{dash(detail.province)}</FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.district')}>{dash(detail.district)}</FieldBlock>
          <div />
          <div />

          <FieldBlock label={t('legalKnowledge.viewOutcome.caseNumber')}>{dash(detail.case_number)}</FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.otherExternalRefNos')}>
            {dash(detail.other_external_reference_numbers)}
          </FieldBlock>
          <FieldBlock label={t('legalKnowledge.viewOutcome.connectedAgdFileRefNo')}>
            {dash(detail.connected_agd_file_reference_number)}
          </FieldBlock>
          {showCourtFields ? (
            <>
              <FieldBlock label={t('legalKnowledge.viewOutcome.courtType')}>{dash(detail.court_type)}</FieldBlock>
              <FieldBlock label={t('legalKnowledge.viewOutcome.courtName')}>{dash(detail.court_name)}</FieldBlock>
            </>
          ) : (
            <div />
          )}

          {showLitigationFields ? (
            <>
              <div style={{ gridColumn: 'span 2' }}>
                <FieldBlock label={t('legalKnowledge.viewOutcome.subjectOfDispute')}>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{dash(detail.subject_of_the_dispute)}</span>
                </FieldBlock>
              </div>
              <FieldBlock label={t('legalKnowledge.viewOutcome.incidentOccurredDate')}>
                {detail.incident_occurred_date ? formatDisplayDate(detail.incident_occurred_date) : '—'}
              </FieldBlock>
              <div />
            </>
          ) : null}

          <div style={{ gridColumn: '1 / -1' }}>
            <FieldBlock label={t('legalKnowledge.viewOutcome.applicableStatutes')}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{dash(detail.applicable_statutes)}</span>
            </FieldBlock>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <FieldBlock label={t('legalKnowledge.viewOutcome.extractedOutcomeSummary')}>
              <span style={{ whiteSpace: 'pre-wrap' }}>{dash(detail.extracted_outcome_summary)}</span>
            </FieldBlock>
          </div>

          {isRejected ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <FieldBlock label={t('legalKnowledge.viewArchived.rejectReasonLabel')}>
                <span style={{ color: 'var(--red)', whiteSpace: 'pre-wrap' }}>{dash(detail.reject_reason)}</span>
              </FieldBlock>
            </div>
          ) : null}
        </div>
      </SectionCard>

      <SectionCard title={t('legalKnowledge.viewOutcome.caseLinkages')}>
        {detail.case_linkages.length === 0 ? (
          <div style={{ color: 'var(--t3)', fontSize: 13 }}>{t('legalKnowledge.viewOutcome.noCaseLinkages')}</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {detail.case_linkages.map((link) => (
              <CaseLinkageLink
                key={link.linkage_id}
                archiveReferenceNumber={link.archive_reference_number}
                targetId={linkageTargetIds[link.linkage_id]}
                resolving={linkageTargetsLoading && !linkageTargetIds[link.linkage_id]}
              />
            ))}
          </div>
        )}
      </SectionCard>

      <SystemInformationSection>
        <SystemInfoBox
          label={t('legalKnowledge.viewOutcome.lastUpdatedAt')}
          value={detail.last_updated_at ? formatDisplayDateTime(detail.last_updated_at) : '—'}
        />
        <SystemInfoBox
          label={t('legalKnowledge.viewOutcome.lastUpdatedBy')}
          value={dash(detail.last_updated_by)}
        />
      </SystemInformationSection>
    </>
  );
}
